/** Partner privacy: consent grants for MultiDoc staff, and the access log. */

import { api, post } from "@/api/client";

export type AreaId = "pursuits" | "files" | "kb" | "crm" | "instances";

export interface Area { id: AreaId; label: string }
export interface Areas { areas: Area[]; durations_hours: number[]; max_hours: number; enforced: boolean }

export interface Person { user_id?: string; email?: string; name?: string; vendor?: boolean }

export interface Grant {
  _id: string;
  org_id: string;
  org_name?: string;
  areas: AreaId[];
  area_labels: string[];
  reason: string;
  hours: number;
  status: "requested" | "active" | "declined" | "revoked" | "expired";
  requested_by?: Person | null;
  requested_at?: string;
  approved_by?: Person;
  approved_at?: string;
  expires_at?: string;
  declined_by?: Person;
  revoked_by?: Person;
  note?: string;
  grantee_ids?: string[];
}

export interface LogRow {
  org_id: string;
  seq: number;
  ts: string;
  kind: string;
  area?: AreaId | null;
  actor: Person;
  method?: string;
  path?: string;
  resource?: { type?: string; id?: string; label?: string };
  grant_id?: string | null;
  ip?: string | null;
  details?: Record<string, unknown>;
  prev: string;
  hash: string;
}

export interface Head { seq: number; hash: string; ts?: string | null }
export interface LogPage { org_id: string; head: Head; rows: LogRow[] }
export interface Verification {
  org_id: string; ok: boolean; rows: number; head?: string;
  first_bad_seq?: number | null; problem?: string | null;
}
export interface Mine { org_id: string; areas: AreaId[]; grants: Grant[]; pending: Grant[] }

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : "";
};

// Staff read a partner's grants and log as themselves, never "from inside" it,
// so these calls say whose they want in the query and send no working org.
const asSelf = { headers: { "X-Org-Id": "" } };

export interface Encryption {
  org_id: string; enabled: boolean; keystore: string | null; algorithm: string;
  versions: { version: number; state: string; created_at?: string; retired_at?: string | null; root_key?: string }[];
}
export const encryption = (orgId?: string) =>
  api<Encryption>(`/access/encryption${qs({ org_id: orgId })}`, asSelf);

export const areas = () => api<Areas>("/access/areas", asSelf);
export const listGrants = (orgId?: string, status?: string) =>
  api<{ grants: Grant[] }>(`/access/grants${qs({ org_id: orgId, status })}`, asSelf);
export const mine = (orgId: string) => api<Mine>(`/access/mine${qs({ org_id: orgId })}`, asSelf);
export const readLog = (params: { orgId?: string; staffOnly?: boolean; kind?: string; beforeSeq?: number }) =>
  api<LogPage>(`/access/log${qs({ org_id: params.orgId, staff_only: params.staffOnly || undefined,
                                  kind: params.kind, before_seq: params.beforeSeq })}`, asSelf);
export const verifyLog = (orgId?: string) =>
  api<Verification>(`/access/log/verify${qs({ org_id: orgId })}`, asSelf);
export const exportUrl = (orgId?: string) => `/api/access/log/export${qs({ org_id: orgId })}`;

export const askForAccess = (body: { org_id: string; areas: AreaId[]; reason: string; hours: number }) =>
  api<Grant>("/access/requests", { method: "POST", body, ...asSelf });
export const openAccess = (body: { areas: AreaId[]; hours: number; reason: string }) =>
  post<Grant>("/access/grants", body);
export const approve = (id: string, body: { hours?: number; areas?: AreaId[] }) =>
  post<Grant>(`/access/grants/${id}/approve`, body);
export const decline = (id: string, note: string) =>
  post<Grant>(`/access/grants/${id}/decline`, { note });
export const revoke = (id: string) =>
  api<Grant>(`/access/grants/${id}/revoke`, { method: "POST", ...asSelf });

export const hoursLabel = (hours: number) =>
  hours < 24 ? `${hours} hour${hours === 1 ? "" : "s"}` : `${hours / 24} day${hours === 24 ? "" : "s"}`;

export const AREA_SHORT: Record<AreaId, string> = {
  pursuits: "Pursuits", files: "Files", kb: "Knowledge base", crm: "CRM", instances: "Demo instances",
};

export const KIND_LABEL: Record<string, string> = {
  "staff.read": "Viewed",
  "staff.write": "Changed",
  "grant.requested": "Asked for access",
  "grant.approved": "Approved access",
  "grant.opened": "Opened access",
  "grant.declined": "Declined access",
  "grant.revoked": "Ended access",
  "grant.expired": "Access expired",
};
